import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import moment from 'moment'

// Constants
import { DASHED_DATE_FORMAT } from 'common/constants/dateFormat'

// Store
import { actions } from 'core/store'

// Hooks
import { usePostAddressReport, useGetAddressHistory } from 'core/hooks/api'

// Styled Elements
import { ModalBodyWrapper, RowContainer, Label } from './ReportAddressModal.elements'

// Views
import {
  LoadingTablePlaceholder,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Radio,
  Checkbox,
} from 'views/components'

// constants
import { REPORT_DATA_TYPE_OPTIONS, REPORT_DATE_RANGE_OPTIONS } from 'common/constants/formOptions'

// Map Redux Props
const mapStateToProps = (state) => state
const mapDispatchToProps = actions

function ReportAddressModal(props) {
  // Destructure
  const { ui, address, actions } = props

  // Store Actions
  const { toggleAddressReportModal, setAddressReportDetails } = actions

  // Store State
  const { isAddressReportModalOpen } = ui
  const { addressReportDetails } = address

  // Hooks
  const { postAddressReport, postAddressReportData, isPostAddressReportError, isPostAddressReportLoading } =
    usePostAddressReport()
  const { getAddressHistory, getAddressHistoryData, isGetAddressHistoryLoading, isGetAddressHistoryError } =
    useGetAddressHistory()

  // Local State
  const [formValue, setFormValue] = useState({ sections: [], date_range: '' })
  const [isError, setIsError] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [totalAddresses, setTotalAddresses] = useState(null)

  // Functions
  const toggleModal = () => {
    toggleAddressReportModal()
    setAddressReportDetails({})
  }
  const handleSubmit = () => {
    let start_range = moment().subtract(30, 'days').format(DASHED_DATE_FORMAT)
    const end_range = moment().add(1, 'days').format(DASHED_DATE_FORMAT)
    if (REPORT_DATE_RANGE_OPTIONS[0].value === formValue.date_range) {
      start_range = moment().subtract(30, 'days').format(DASHED_DATE_FORMAT)
    }
    if (REPORT_DATE_RANGE_OPTIONS[1].value === formValue.date_range) {
      start_range = moment().subtract(180, 'days').format(DASHED_DATE_FORMAT)
    }
    if (REPORT_DATE_RANGE_OPTIONS[2].value === formValue.date_range) {
      start_range = moment().subtract(365, 'days').format(DASHED_DATE_FORMAT)
    }
    postAddressReport({
      address_id: addressReportDetails.address_id,
      sections: formValue.sections,
      start_range,
      end_range,
    })
  }
  function handleValueChange() {
    if (!formValue?.date_range || !formValue?.sections) return
    let start_range = moment().subtract(30, 'days').format(DASHED_DATE_FORMAT)
    const end_range = moment().add(1, 'days').format(DASHED_DATE_FORMAT)
    if (REPORT_DATE_RANGE_OPTIONS[0].value === formValue.date_range) {
      start_range = moment().subtract(30, 'days').format(DASHED_DATE_FORMAT)
    }
    if (REPORT_DATE_RANGE_OPTIONS[1].value === formValue.date_range) {
      start_range = moment().subtract(180, 'days').format(DASHED_DATE_FORMAT)
    }
    if (REPORT_DATE_RANGE_OPTIONS[2].value === formValue.date_range) {
      start_range = moment().subtract(365, 'days').format(DASHED_DATE_FORMAT)
    }
    getAddressHistory({
      address_id: addressReportDetails.address_id,
      size: 10,
      page: 1,
      sort: '-last_modified',
      start_range,
      end_range,
    })
  }
  function handleHistoryData() {
    if (getAddressHistoryData?.pagination) {
      setTotalAddresses(getAddressHistoryData?.pagination?.total)
    }
  }

  // UseEffects
  useEffect(() => {
    handleHistoryData()
  }, [getAddressHistoryData])
  useEffect(() => {
    if (isGetAddressHistoryError) setTotalAddresses(0)
  }, [isGetAddressHistoryError])
  useEffect(() => {
    handleValueChange()
  }, [formValue])
  useEffect(() => postAddressReportData && setIsSuccess(true), [postAddressReportData])
  useEffect(() => isPostAddressReportError && setIsError(true), [isPostAddressReportError])
  useEffect(() => {
    if (isAddressReportModalOpen) {
      setFormValue({ sections: [], date_range: '', format: '' })
      setIsError(false)
      setIsSuccess(false)
      setTotalAddresses(null)
    }
  }, [isAddressReportModalOpen])
  return (
    <Modal isOpen={isAddressReportModalOpen} toggle={toggleModal}>
      <ModalHeader toggle={toggleModal}>REQUEST REPORT</ModalHeader>
      <ModalBody>
        {isError && !isPostAddressReportLoading && (
          <ModalBodyWrapper style={{ display: 'flex', justifyContent: 'center', padding: 0 }}>
            An error occurred in generating a report. Please try again later.
          </ModalBodyWrapper>
        )}
        {isSuccess && !isPostAddressReportLoading && (
          <ModalBodyWrapper style={{ display: 'flex', justifyContent: 'center', padding: 0 }}>
            <span>
              Successfully requested for report, generating the report may take a few minutes. Please view the progress
              in the <a href="/reports">reports tab</a>.
            </span>
          </ModalBodyWrapper>
        )}
        {isPostAddressReportLoading && (
          <ModalBodyWrapper style={{ display: 'flex', justifyContent: 'center', padding: 0 }}>
            <LoadingTablePlaceholder style={{ padding: '45px 0' }} />
          </ModalBodyWrapper>
        )}
        {!isPostAddressReportLoading && !isError && !isSuccess && (
          <ModalBodyWrapper>
            <RowContainer style={{ justifyContent: 'flex-start', gap: '25px' }}>
              <Checkbox
                value={formValue.sections}
                onChange={(value) => setFormValue({ ...formValue, sections: value })}
                label="DATA TYPE"
                options={REPORT_DATA_TYPE_OPTIONS}
                name="sections"
              />
              <Radio
                value={formValue.date_range}
                onChange={(value) => setFormValue({ ...formValue, date_range: value })}
                label="DATA RANGE"
                options={REPORT_DATE_RANGE_OPTIONS}
                name="date_range"
              />
            </RowContainer>
            {totalAddresses !== null && !isGetAddressHistoryLoading && totalAddresses > 0 && (
              <RowContainer style={{ justifyContent: 'flex-start', gap: '50px' }}>
                <Label>Total Number of Records: {totalAddresses}</Label>
              </RowContainer>
            )}
            {totalAddresses < 1 && totalAddresses !== null && !isGetAddressHistoryLoading && (
              <RowContainer style={{ justifyContent: 'flex-start', gap: '50px' }}>
                <Label error>Given Date Range Does Not Have Any Record</Label>
              </RowContainer>
            )}
            {isGetAddressHistoryLoading && (
              <RowContainer style={{ justifyContent: 'flex-start', gap: '50px' }}>
                <Label>Fetching Total Number of Addresses</Label>
              </RowContainer>
            )}
          </ModalBodyWrapper>
        )}
      </ModalBody>
      <ModalFooter>
        {!isPostAddressReportLoading && !isError && !isSuccess && (
          <>
            <Button fullWidth type="button" variant="outlined" onClick={toggleModal}>
              Cancel
            </Button>
            <Button
              fullWidth
              type="button"
              onClick={handleSubmit}
              disabled={!totalAddresses || totalAddresses < 1 || formValue.sections.length < 1 || !formValue.date_range}
            >
              Request
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  )
}

// Default Props
ReportAddressModal.defaultProps = {
  ui: {},
  address: {},
  actions: {},
}

// Proptypes Validation
ReportAddressModal.propTypes = {
  ui: PropTypes.shape({ isAddressReportModalOpen: PropTypes.bool }),
  address: PropTypes.shape({
    addressReportDetails: PropTypes.shape({
      address_id: PropTypes.string,
      address: PropTypes.string,
      chain: PropTypes.string,
    }),
  }),
  actions: PropTypes.shape({
    toggleAddressReportModal: PropTypes.func,
    setIsPageTableUpdated: PropTypes.func,
    setAddressReportDetails: PropTypes.func,
    setShowHeaderLoader: PropTypes.func,
  }),
}

export default connect(mapStateToProps, mapDispatchToProps)(ReportAddressModal)
